/// <amd-module name="typeahead/core/google/GoogleMapsDataProvider" />
import {GoogleRequestType} from "typeahead/core/EssDataProvider";
import {AddressAutocompleteFailureMetric} from "typeahead/core/google/AddressAutocompleteFailureMetric";
import {POWERED_BY_GOOGLE_LOGO} from "typeahead/core/google/PoweredByGoogleLogo";
import {IDataProvider, IDataProviderResponse} from "typeahead/core/IDataProvider";
import {IListItem, LabelTypeListItem, ListItemType} from "typeahead/core/IListItem";
import {CollectorTransport} from "typeahead/core/logging/CollectorTransport";
import {ProblemOrigin} from "typeahead/core/logging/ProblemOrigin";
import {Severity} from "typeahead/core/logging/Severity";
import TypeaheadErrorLogger from "typeahead/core/logging/TypeaheadErrorLogger";
import {ISuggestionResult, SuggestionCategory} from "typeahead/core/Suggestion";
import AutocompletePrediction = google.maps.places.AutocompletePrediction;
import PredictionSubstring = google.maps.places.PredictionSubstring;
import AutocompleteService = google.maps.places.AutocompleteService;

// Load and execute google maps api script
$.getScript("https://maps.googleapis.com/maps/api/js?client=gme-expedia&channel=ess&libraries=places");

export interface IGoogleMapsDataProviderOptions {
    activeEssTests: string;
    domElementId: string;
    lob: string;
    requestType: string;
    srchText: string;
}

export interface IGoogleMapsSuggestionResult extends ISuggestionResult {
    sourceId: string;
    sourceName: string;
}

export interface IGoogleMapsDataProviderResponse extends IDataProviderResponse {
    query: string;
}

export class GoogleMapsDataProvider implements IDataProvider {

    private static readonly MAX_RESULTS: number = 5;

    private static getSuggestionResult(prediction: AutocompletePrediction, index): IGoogleMapsSuggestionResult {
        return  {
            category: SuggestionCategory[SuggestionCategory.GOOGLEMAPS],
            coordinates: undefined,
            listItemType: ListItemType.Suggestion,
            regionNames: {
                displayName: GoogleMapsDataProvider.boldMatchedSubStringInPrediction(prediction),
                fullName: prediction.description,
                lastSearchName : prediction.description,
            },
            sourceId: prediction.place_id,
            sourceName: "GPA",
            type : "ADDRESS",
            index,
        };
    }

    private static boldMatchedSubStringInPrediction(prediction) {
        let displayname: string = prediction.description;
        let result = "";
        let connectHead = 0;
        let matchedSubstrings: PredictionSubstring[] = prediction.matched_substrings;
        let matchedSubstring: PredictionSubstring;
        for (let index: number = 0; index < matchedSubstrings.length; index++) {
            matchedSubstring = matchedSubstrings[index];
            result += displayname.slice(connectHead, matchedSubstring.offset);
            result += "<B>" + displayname.slice(matchedSubstring.offset, matchedSubstring.offset +
                    matchedSubstring.length) + "</B>";
            connectHead = matchedSubstring.offset + matchedSubstring.length;
        }

        return result + displayname.slice(connectHead);
    }

    private googleAutocompleteService: AutocompleteService;

    public fetchData(options: IGoogleMapsDataProviderOptions): JQueryPromise<IGoogleMapsDataProviderResponse> {
        let deferred: JQueryDeferred<IGoogleMapsDataProviderResponse> = $.Deferred();
        let request = {
            input: options.srchText,
            types: GoogleRequestType[GoogleRequestType.ADDRESS_AUTOCOMPLETE] === options.requestType ?
                ["geocode"] : undefined,
        };

        if (google && google.maps && google.maps.places && google.maps.places.AutocompleteService) {
            this.getGoogleAutocompleteService().getPlacePredictions(request,
                (predictions: AutocompletePrediction[], status: google.maps.places.PlacesServiceStatus) => {
                    this.handlePredictionsResponse(predictions, status, options, deferred);
                });
        } else {
            TypeaheadErrorLogger.log(
                new Error("Google maps script not loaded"), Severity.Excusable, ProblemOrigin.UniversalTypeahead);
        }

        return deferred.promise();
    }

    private getGoogleAutocompleteService(): AutocompleteService {
        if (!this.googleAutocompleteService) {
            this.googleAutocompleteService = new google.maps.places.AutocompleteService();
        }
        return this.googleAutocompleteService;
    }

    private handlePredictionsResponse(predictions: AutocompletePrediction[],
                                      status: google.maps.places.PlacesServiceStatus,
                                      options: IGoogleMapsDataProviderOptions,
                                      deferred: JQueryDeferred<IGoogleMapsDataProviderResponse>) {

        let suggestionResults: IListItem[] = [];

        if (status === google.maps.places.PlacesServiceStatus.OK) {
            // status = ZERO_RESULT is treated as a failure as we only log failures for now.
            // In failure metrics we will get to know how often ZERO_RESULT happens.

            if (predictions) {
                let suggestionCount: number = Math.min(GoogleMapsDataProvider.MAX_RESULTS, predictions.length);
                for (let i: number = 0; i < suggestionCount; i++) {
                    suggestionResults.push(GoogleMapsDataProvider.getSuggestionResult(predictions[i], i));
                }
            }
        } else {
            new CollectorTransport().log(new AddressAutocompleteFailureMetric(
                options.lob,
                options.domElementId,
                options.srchText,
                google.maps.places.PlacesServiceStatus[status],
                options.activeEssTests));
        }
        if (suggestionResults && suggestionResults.length) {
            suggestionResults.push(this.getPoweredByGoogleListItem());
        }

        deferred.resolve({query: options.srchText, results: suggestionResults});
    }

    private getPoweredByGoogleListItem(): LabelTypeListItem {
        let getHtml = () => {
            return "<div style='text-align:right;position:relative;top:0.5em;'>" +
                "<img src='" + POWERED_BY_GOOGLE_LOGO + "'/></div>";
        };
        return new LabelTypeListItem(getHtml());
    }
}
